import { useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import type { ColumnDef, SortingState, Updater } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type { ProductFilter, ProductSort, SortDirection } from '@/gql/graphql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { formatCurrency } from '@/lib/format';
import { downloadAuthenticatedCsv } from '@/lib/downloadCsv';
import { useListHref } from '@/hooks/useListLocation';
import {
  isDefault,
  sortingFromUrl,
  sortingToUrl,
  useDebouncedUrlField,
  useUrlState,
  type ActiveVisibility,
} from '@/hooks/useUrlState';
import {
  PRODUCT_COLUMN_SORT,
  PRODUCTS_ADVANCED_KEYS,
  PRODUCTS_DEFAULT_SORT,
  productsUrlSchema,
} from './productsUrlState';

const ProductsDoc = graphql(/* GraphQL */ `
  query Products(
    $filter: ProductFilter
    $includeInactive: Boolean
    $page: Int
    $pageSize: Int
    $sort: ProductSort
  ) {
    products(
      filter: $filter
      includeInactive: $includeInactive
      page: $page
      pageSize: $pageSize
      sort: $sort
    ) {
      nodes {
        id
        name
        price
        tax
        isActive
        categories {
          id
          name
        }
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

const CategoriesForProductFilterDoc = graphql(/* GraphQL */ `
  query CategoriesForProductFilter {
    categories(includeInactive: true) {
      id
      name
    }
  }
`);

const SetProductsActiveDoc = graphql(/* GraphQL */ `
  mutation SetProductsActive($ids: [ID!]!, $isActive: Boolean!) {
    setProductsActive(ids: $ids, isActive: $isActive) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`);

const ExportProductsCsvDoc = graphql(/* GraphQL */ `
  query ExportProductsCsv($filter: ProductFilter, $sort: ProductSort) {
    exportProductsCsv(filter: $filter, sort: $sort) {
      url
      expiresAt
    }
  }
`);

const filterSectionTitle =
  'mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
const filterLabel = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200';

interface ProductRow {
  id: string;
  name: string;
  price: number;
  tax: number;
  isActive: boolean;
  categories: Array<{ id: string; name: string }>;
}

type ProductAction = 'SET_ACTIVE' | 'SET_INACTIVE';

export function ProductsListPage() {
  const apollo = useApolloClient();
  const navigate = useNavigate();
  const { state: url, patch, setFilters, reset } = useUrlState(productsUrlSchema);
  const listHref = useListHref();
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));
  const [priceMin, setPriceMin] = useDebouncedUrlField(url.priceMin, (next) => setFilters({ priceMin: next }));
  const [priceMax, setPriceMax] = useDebouncedUrlField(url.priceMax, (next) => setFilters({ priceMax: next }));
  const [taxMin, setTaxMin] = useDebouncedUrlField(url.taxMin, (next) => setFilters({ taxMin: next }));
  const [taxMax, setTaxMax] = useDebouncedUrlField(url.taxMax, (next) => setFilters({ taxMax: next }));
  // The Filters panel opens on mount when a filter inside it is set (a reload, Back, an inbound link).
  const [filtersOpen] = useState(() => PRODUCTS_ADVANCED_KEYS.some((key) => !isDefault(productsUrlSchema, url, key)));
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const sorting = useMemo(() => sortingFromUrl(url), [url]);
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setFilters(sortingToUrl(next, PRODUCTS_DEFAULT_SORT));
  };

  const includeInactive = url.show !== 'active';

  const filter = useMemo((): ProductFilter => {
    const out: ProductFilter = {};
    if (url.search.trim()) {
      out.search = url.search.trim();
    }
    if (url.priceMin !== '' || url.priceMax !== '') {
      out.price = {
        min: url.priceMin === '' ? null : Number(url.priceMin),
        max: url.priceMax === '' ? null : Number(url.priceMax),
      };
    }
    if (url.taxMin !== '' || url.taxMax !== '') {
      out.tax = {
        min: url.taxMin === '' ? null : Number(url.taxMin),
        max: url.taxMax === '' ? null : Number(url.taxMax),
      };
    }
    if (url.category) {
      out.categoryIds = [url.category];
    }
    if (url.show === 'inactive') {
      out.isActive = false;
    }
    return out;
  }, [url]);

  const gqlSort = useMemo((): ProductSort => {
    const direction: SortDirection = url.dir === 'desc' ? 'DESC' : 'ASC';
    return { field: PRODUCT_COLUMN_SORT[url.sort], direction };
  }, [url.sort, url.dir]);

  const exportFilter = useMemo((): ProductFilter => {
    return {
      ...filter,
      includeInactive,
    };
  }, [filter, includeInactive]);

  const { data, previousData, loading, refetch } = useQuery(ProductsDoc, {
    variables: {
      filter,
      includeInactive,
      page: url.page,
      pageSize: url.size,
      sort: gqlSort,
    },
  });
  const productsData = data ?? previousData;
  const { data: catsData } = useQuery(CategoriesForProductFilterDoc);
  const [setProductsActive] = useMutation(SetProductsActiveDoc);

  const rows: ProductRow[] = useMemo(
    () =>
      (productsData?.products.nodes ?? []).map((n) => ({
        id: n.id,
        name: n.name,
        price: n.price,
        tax: n.tax,
        isActive: n.isActive,
        categories: n.categories.map((c) => ({ id: c.id, name: c.name })),
      })),
    [productsData],
  );
  const pageInfo = productsData?.products.pageInfo;

  const columns = useMemo<ColumnDef<ProductRow>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/products/${row.original.id}`}
            state={{ from: listHref }}
            className="font-medium text-gray-900 hover:text-brand dark:text-white"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: 'price',
        accessorKey: 'price',
        header: 'Price',
        cell: ({ row }) => formatCurrency(row.original.price),
      },
      {
        id: 'tax',
        accessorKey: 'tax',
        header: 'Tax',
        cell: ({ row }) => `${row.original.tax.toFixed(2)}%`,
      },
      {
        id: 'isActive',
        accessorKey: 'isActive',
        header: 'Active',
        cell: ({ row }) => (
          <Badge tone={row.original.isActive ? 'success' : 'neutral'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        id: 'categories',
        accessorFn: (row) => row.categories.map((c) => c.name).join(', '),
        header: 'Categories',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.categories.map((c) => c.name).join(', ') || '—',
      },
    ],
    [listHref],
  );

  const handleExport = async () => {
    const { data: exportData } = await apollo.query({
      query: ExportProductsCsvDoc,
      variables: { filter: exportFilter, sort: gqlSort },
      fetchPolicy: 'network-only',
    });
    if (exportData?.exportProductsCsv?.url) {
      await downloadAuthenticatedCsv(exportData.exportProductsCsv.url);
    }
  };

  const runBulkActive = async (ids: string[], active: boolean) => {
    await setProductsActive({ variables: { ids, isActive: active } });
    setSelection({});
    await refetch();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Products
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            The catalogue that drives the POS. Open a product to edit prices, tax and
            flags. Use bulk actions to activate or deactivate selected rows.
          </p>
        </div>
        <Button
          id="admin.products.new"
          onClick={() => navigate('/products/new', { state: { from: listHref } })}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          New product
        </Button>
      </div>

      <FilterBar
        defaultOpen={filtersOpen}
        search={{
          id: 'admin.products.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Search products…',
        }}
        primary={
          <button
            type="button"
            onClick={reset}
            className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-xs transition-colors hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Reset all filters
          </button>
        }
        advanced={
          <div className="w-full min-w-0 space-y-10">
            <section className="min-w-0" aria-labelledby="products-filters-visibility-heading">
              <h3 id="products-filters-visibility-heading" className={filterSectionTitle}>
                Catalogue visibility
              </h3>
              <div className="grid w-full grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                <label className="min-w-0 lg:col-span-3">
                  <span className={filterLabel}>Active status</span>
                  <Select
                    id="admin.products.show"
                    className="w-full min-w-0"
                    value={url.show}
                    onChange={(e) =>
                      setFilters({ show: e.target.value as ActiveVisibility })
                    }
                    options={[
                      { value: 'active', label: 'Active only' },
                      { value: 'inactive', label: 'Inactive only' },
                      { value: 'all', label: 'All products' },
                    ]}
                  />
                </label>
                <label className="min-w-0 lg:col-span-3">
                  <span className={filterLabel}>Category</span>
                  <Select
                    id="admin.products.category"
                    className="w-full min-w-0"
                    value={url.category}
                    onChange={(event) => setFilters({ category: event.target.value })}
                    options={[
                      { value: '', label: 'Any category' },
                      ...(catsData?.categories.map((c) => ({
                        value: c.id,
                        label: c.name,
                      })) ?? []),
                    ]}
                  />
                </label>
              </div>
            </section>

            <section
              className="min-w-0 border-t border-gray-200/90 pt-8 dark:border-white/10"
              aria-labelledby="products-filters-pricing-heading"
            >
              <h3 id="products-filters-pricing-heading" className={filterSectionTitle}>
                Price and tax
              </h3>
              <div className="grid w-full grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4">
                <label className="min-w-0">
                  <span className={filterLabel}>Price min</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={priceMin}
                    onChange={(event) => setPriceMin(event.target.value)}
                    className="w-full min-w-0"
                  />
                </label>
                <label className="min-w-0">
                  <span className={filterLabel}>Price max</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={priceMax}
                    onChange={(event) => setPriceMax(event.target.value)}
                    className="w-full min-w-0"
                  />
                </label>
                <label className="min-w-0">
                  <span className={filterLabel}>Tax min (%)</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={taxMin}
                    onChange={(event) => setTaxMin(event.target.value)}
                    className="w-full min-w-0"
                  />
                </label>
                <label className="min-w-0">
                  <span className={filterLabel}>Tax max (%)</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={taxMax}
                    onChange={(event) => setTaxMax(event.target.value)}
                    className="w-full min-w-0"
                  />
                </label>
              </div>
            </section>
          </div>
        }
      />

      <DataGrid<ProductRow, ProductAction>
        data={rows}
        columns={columns}
        loading={loading && !productsData}
        rowId={(row) => row.id}
        onExport={handleExport}
        selection={{ state: selection, onChange: setSelection }}
        sorting={{ state: sorting, onChange: handleSortingChange }}
        actions={[
          {
            id: 'SET_ACTIVE',
            label: 'Activate',
            variant: 'outline',
            onRun: (selected) =>
              runBulkActive(
                selected.map((r) => r.id),
                true,
              ),
          },
          {
            id: 'SET_INACTIVE',
            label: 'Deactivate',
            variant: 'outline',
            onRun: (selected) =>
              runBulkActive(
                selected.map((r) => r.id),
                false,
              ),
          },
        ]}
        allowedActionsForRow={(row) =>
          row.isActive ? ['SET_INACTIVE'] : ['SET_ACTIVE']
        }
        rowMuted={(row) => !row.isActive}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
      />
    </div>
  );
}
